import React from "react";
import { Link } from "react-router-dom";
import { UserPlus, Eye, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

/**
 * Demo-mode gate shown to non-logged-in visitors (Guests).
 * Friendly invitation to register, no aggressive pop-up — inline placement.
 */
const TR = {
  sk: {
    tag: "UKÁŽKA",
    title: "Tu by si videl plný výsledok",
    msg: "Ahoj zvedavec! 👀 Toto je len ukážka, čo dokáže Tigik. Po registrácii dostaneš plný prístup ku všetkým kalkulačkám, AI poradcovi a komunite.",
    cta: "Zaregistrovať sa zadarmo",
    secondary: "Mám účet — prihlásiť sa",
  },
  en: {
    tag: "PREVIEW",
    title: "Here you'd see the full result",
    msg: "Hi curious one! 👀 This is just a sneak peek of what Tigik can do. Once registered, you get full access to all calculators, AI advisor and community.",
    cta: "Sign up — it's free",
    secondary: "I have an account — log in",
  },
  cs: {
    tag: "UKÁZKA",
    title: "Tady bys viděl plný výsledek",
    msg: "Ahoj zvědavče! 👀 Toto je jen ukázka, co umí Tigik. Po registraci dostaneš plný přístup ke všem kalkulačkám, AI poradci a komunitě.",
    cta: "Zaregistrovat se zdarma",
    secondary: "Mám účet — přihlásit se",
  },
  de: {
    tag: "VORSCHAU",
    title: "Hier würdest du das volle Ergebnis sehen",
    msg: "Hallo Neugieriger! 👀 Das ist nur ein Vorgeschmack auf das, was Tigik kann. Nach der Registrierung hast du vollen Zugang zu allen Rechnern, KI-Berater und Community.",
    cta: "Kostenlos registrieren",
    secondary: "Ich habe ein Konto — anmelden",
  },
  pl: {
    tag: "PODGLĄD",
    title: "Tutaj zobaczyłbyś pełny wynik",
    msg: "Cześć ciekawski! 👀 To tylko podgląd tego, co potrafi Tigik. Po rejestracji uzyskasz pełny dostęp do wszystkich kalkulatorów, doradcy AI i społeczności.",
    cta: "Zarejestruj się za darmo",
    secondary: "Mam konto — zaloguj się",
  },
  fr: {
    tag: "APERÇU",
    title: "Tu verrais ici le résultat complet",
    msg: "Salut curieux ! 👀 Ceci n'est qu'un aperçu de ce que Tigik peut faire. Après inscription, tu auras un accès complet à toutes les calculatrices, au conseiller IA et à la communauté.",
    cta: "S'inscrire gratuitement",
    secondary: "J'ai un compte — se connecter",
},
};

export default function GuestGate({ compact = false, customMsg }) {
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;

  return (
    <div
      data-testid="guest-gate"
      className={`border border-gold-700/50 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black relative overflow-hidden ${compact ? "p-4" : "p-6"}`}
    >
      <div className="absolute inset-0 wb-grid-bg opacity-10 pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <div className="w-10 h-10 bg-gold-500/15 border border-gold-600 flex items-center justify-center flex-shrink-0">
          <Eye className="w-5 h-5 text-gold-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="wb-tag !text-gold-300 !border-gold-500">{tr.tag}</span>
          </div>
          <h3 className="font-display font-bold text-base text-white">{tr.title}</h3>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{customMsg || tr.msg}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/register"
              data-testid="guest-gate-register"
              className="wb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-xs"
            >
              <UserPlus className="w-3.5 h-3.5" /> {tr.cta} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/login"
              data-testid="guest-gate-login"
              className="wb-btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-xs"
            >
              {tr.secondary}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
